/**
 * Hook for managing substitution overlay data in the timetable view.
 * Fetches posted assignments for a selected date and provides helper functions
 * to check if a period has a substitution.
 * 
 * Requirements: 17.1, 17.4, 17.5
 */

import { useQuery } from '@tanstack/react-query';
import { getPostedAssignmentsForDate } from '@timetable/api';
import { queryKeys } from '../lib/queryKeys';
import type { DraftAssignment, DayOfWeek } from '@timetable/types';

interface SubstitutionOverlayData {
  /** All posted assignments for the selected date */
  assignments: DraftAssignment[];
  /** Check if a specific period has a substitution */
  hasSubstitution: (day: DayOfWeek, periodId: string, teacherId?: string, classSectionId?: string) => boolean;
  /** Get substitution details for a specific period */
  getSubstitution: (day: DayOfWeek, periodId: string, teacherId?: string, classSectionId?: string) => DraftAssignment | undefined;
  /** Check if a teacher is covering for someone on this date */
  isTeacherCovering: (teacherId: string, day: DayOfWeek, periodId: string) => DraftAssignment | undefined;
  /** Check if a teacher is absent on this date */
  isTeacherAbsent: (teacherId: string) => boolean;
  /** Get all absences for a specific teacher on this date */
  getTeacherAbsences: (teacherId: string) => DraftAssignment[];
  /** Loading state */
  isLoading: boolean;
}

export function useSubstitutionOverlay(
  schoolId: string | null,
  selectedDate: Date | null
): SubstitutionOverlayData {
  const dateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : null;

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: queryKeys.assignments.forDate(schoolId || '', dateStr || ''),
    queryFn: () => getPostedAssignmentsForDate(schoolId!, dateStr!),
    enabled: !!schoolId && !!dateStr,
  });

  const hasSubstitution = (
    day: DayOfWeek,
    periodId: string,
    teacherId?: string,
    classSectionId?: string
  ): boolean => {
    return assignments.some((a) => {
      // Match by period and either teacher or class section
      const periodMatch = a.period_id === periodId;
      const teacherMatch = teacherId ? a.absent_teacher_id === teacherId : true;
      const classMatch = classSectionId ? a.class_section_id === classSectionId : true;
      
      return periodMatch && teacherMatch && classMatch;
    });
  };

  const getSubstitution = (
    day: DayOfWeek,
    periodId: string,
    teacherId?: string,
    classSectionId?: string
  ): DraftAssignment | undefined => {
    return assignments.find((a) => {
      const periodMatch = a.period_id === periodId;
      const teacherMatch = teacherId ? a.absent_teacher_id === teacherId : true;
      const classMatch = classSectionId ? a.class_section_id === classSectionId : true;
      
      return periodMatch && teacherMatch && classMatch;
    });
  };

  const isTeacherCovering = (
    teacherId: string,
    day: DayOfWeek,
    periodId: string
  ): DraftAssignment | undefined => {
    return assignments.find(
      (a) => a.substitute_teacher_id === teacherId && a.period_id === periodId
    );
  };

  const isTeacherAbsent = (teacherId: string): boolean => {
    return assignments.some((a) => a.absent_teacher_id === teacherId);
  };

  const getTeacherAbsences = (teacherId: string): DraftAssignment[] => {
    return assignments.filter((a) => a.absent_teacher_id === teacherId);
  };

  return {
    assignments,
    hasSubstitution,
    getSubstitution,
    isTeacherCovering,
    isTeacherAbsent,
    getTeacherAbsences,
    isLoading,
  };
}
